/**
 * Perfil de conteúdo (+18 / familiar) — nome comercial limpo e selos automáticos.
 * O interruptor is_adult no catálogo manda; não é preciso duplicar nomes com "S/+18".
 * content_choice_enabled = um plano com escolha Familiar/Adulto na criação.
 */

const ADULT_LABEL_SUFFIX_RE =
  /\s*[-–—]\s*(?:S\/?\+18|SEM\s*\+18|Sem\s*\+18|sem\s*adulto|com\s*\+18|\+18)\s*$|\s*\(\+18\)\s*$|\s*\+18\s*$|\s*🔞\s*$/i;

/** Remove sufixos manuais de +18 / sem +18 do nome comercial. */
export function cleanPlanCommercialLabel(label) {
  const raw = String(label || '').trim();
  if (!raw) return '';
  const out = raw.replace(ADULT_LABEL_SUFFIX_RE, '').trim();
  return out || raw;
}

/** Inferência legada a partir do texto (importação Servidor / planos antigos). */
export function inferPlanAdultFromText(...parts) {
  const combined = parts.map((p) => String(p || '')).join(' ').toLowerCase();
  if (!combined.trim()) return null;
  if (/\bsem\s*\+18\b|\bs\/\+18\b|\bsem adulto\b|\bfamiliar\b|\bsem \+18\b/.test(combined)) {
    return false;
  }
  if (/\+18|\badult\b|🔞|\bxxx\b/.test(combined)) {
    return true;
  }
  return null;
}

/** Plano com escolha Familiar/Adulto no momento da venda (1 SKU). */
export function planHasContentChoice(plan) {
  if (!plan) return false;
  return (
    plan.content_choice_enabled === true ||
    plan.content_choice_enabled === 1 ||
    plan.content_profile?.content_choice === true ||
    plan.content_profile?.tone === 'choice'
  );
}

export function planIsAdult(plan) {
  if (!plan) return false;
  if (planHasContentChoice(plan)) return false;
  if (plan.is_adult === true) return true;
  if (plan.is_adult === false) return false;
  const inferred = inferPlanAdultFromText(plan.label, plan.package_name, plan.xui_origin_name);
  if (inferred !== null) return inferred;
  return /\+18|adult|🔞/i.test(`${plan.label || ''} ${plan.package_name || ''}`);
}

export function getPlanContentProfile(plan) {
  if (planHasContentChoice(plan)) {
    return {
      isAdult: false,
      contentChoice: true,
      tone: 'choice',
      emoji: '🔀',
      badge: 'Familiar ou Adulto',
      shortBadge: 'Escolha',
      hint: 'Na criação o cliente escolhe Familiar ou Adulto incluso — um só plano.',
    };
  }
  const isAdult = planIsAdult(plan);
  if (isAdult) {
    return {
      isAdult: true,
      contentChoice: false,
      tone: 'adult',
      emoji: '🔞',
      badge: 'Adulto incluso',
      shortBadge: '+18',
      hint: 'Inclui canais e conteúdos para maiores de 18 anos.',
    };
  }
  return {
    isAdult: false,
    contentChoice: false,
    tone: 'family',
    emoji: '🛡️',
    badge: 'Familiar',
    shortBadge: 'Sem +18',
    hint: 'Catálogo sem conteúdo adulto — indicado para toda a família.',
  };
}

/** Título limpo para vitrine / checkout (sem sufixos manuais). */
export function formatPlanCheckoutTitle(plan) {
  const fromApi = String(plan?.display_label || '').trim();
  if (fromApi) return fromApi;
  return cleanPlanCommercialLabel(plan?.label || plan?.package_name || 'Plano');
}

/** Rótulo para `<select>` — nome, duração e selo adulto/familiar. */
export function formatPlanSelectLabel(plan) {
  if (!plan) return 'Plano';
  const label = formatPlanCheckoutTitle(plan) || String(plan.label || plan.package_name || '').trim();
  const duration = String(plan.duration_label || '').trim();
  const profile = getPlanContentProfile(plan);
  let base = label;
  if (label && duration) base = `${label} · ${duration}`;
  else if (!label) base = duration || 'Plano';
  return `${base} · ${profile.emoji} ${profile.badge}`;
}

/** Normaliza plano ao gravar no catálogo. */
export function normalizePlanForCatalog(plan) {
  const rawLabel = String(plan?.label || '').trim();
  const cleaned = cleanPlanCommercialLabel(rawLabel);
  let isAdult = !!plan?.is_adult;
  if (plan?.is_adult == null || plan?.is_adult === undefined) {
    const inferred = inferPlanAdultFromText(rawLabel, plan?.package_name, plan?.xui_origin_name);
    if (inferred !== null) isAdult = inferred;
  }
  const choice = !!plan?.content_choice_enabled;
  if (choice) isAdult = false;
  return {
    ...plan,
    label: cleaned || rawLabel || 'Plano comercial',
    is_adult: isAdult,
    content_choice_enabled: choice,
  };
}

/** Resolve plano comercial de uma linha (catálogo Office). */
export function resolveClientCatalogPlan(client, catalogPlans) {
  if (!client) return null;
  const plans = catalogPlans || [];
  const cid = String(client.catalog_plan_id || '').trim();
  if (cid) {
    const hit = plans.find((p) => String(p.id) === cid);
    if (hit) return hit;
  }
  const xuiId = String(client.package_id || client.xui_package_id || '').trim();
  if (xuiId) {
    const matches = plans.filter((p) => String(p.xui_package_id || p.package_id || '') === xuiId);
    if (matches.length === 1) return matches[0];
  }
  const name = String(client.package_name || client.plan_display_label || '').trim();
  if (name) {
    const byLabel = plans.filter((p) => String(p.label || '').trim() === name);
    if (byLabel.length === 1) return byLabel[0];
  }
  if (client.plan_is_adult != null || client.plan_content_profile) {
    return {
      label: client.plan_display_label || client.package_name || 'Plano',
      is_adult: client.plan_is_adult,
      duration_label: '',
      content_profile: client.plan_content_profile,
    };
  }
  return null;
}

/** Linha migrada / genérica que precisa de plano comercial vinculado. */
const TRIAL_PLAN_RE =
  /\b(testes?|trials?|tests?|demo|demonstra[cç][aã]o|cortesia)\b/i;
/** Só 1–12h (testes curtos). «C.24H PREMIUM» / canais 24h NÃO são teste. */
const SHORT_TRIAL_HOURS_RE = /\b([1-9]|1[0-2])\s*h(?:oras?|rs?|ours?)?\b/i;

export function clientLooksLikeTrial(client) {
  if (!client) return false;
  if (client.eh_teste === true || client.is_trial === true || client.is_trial === 1 || client.is_trial === '1') {
    return true;
  }
  const blob = `${client.sigma_plan_name || ''} ${client.origin_plan_name || ''} ${client.package_name || ''} ${client.plan_display_label || ''}`;
  if (TRIAL_PLAN_RE.test(blob)) return true;
  return SHORT_TRIAL_HOURS_RE.test(blob);
}

export function clientNeedsPlanSync(client) {
  if (!client) return false;
  if (clientLooksLikeTrial(client)) return false;
  // Backend pode ter marcado false por falso positivo antigo — se for migração
  // sem plano Office, ainda pedimos vínculo.
  const src = String(client.line_source || client.source || '');
  const isMigration = src === 'sigma_api' || src === 'recovery_push' || src === 'm3u_import';
  const cid = String(client.catalog_plan_id || '').trim();
  if (isMigration && !cid) return true;
  if (client.needs_plan_sync === true) return true;
  if (client.needs_plan_sync === false) return false;
  const pkg = String(client.package_name || '').trim().toUpperCase();
  if (!cid && (pkg === 'PLANO' || pkg === 'PLAN' || pkg.startsWith('PLAN ['))) return true;
  return false;
}

/** Cliente de migração que já tem plano comercial vinculado no Office. */
export function clientMigrationLinked(client) {
  if (!client || clientLooksLikeTrial(client)) return false;
  const src = String(client.line_source || client.source || '');
  const isMigration = src === 'sigma_api' || src === 'recovery_push' || src === 'm3u_import';
  if (!isMigration) return false;
  return !!String(client.catalog_plan_id || '').trim();
}

/** Título limpo do plano na listagem (sem emoji duplicado). */
export function formatClientPlanTitle(client, catalogPlans) {
  if (clientNeedsPlanSync(client)) {
    return 'Vincular plano ao cliente';
  }
  const plan = resolveClientCatalogPlan(client, catalogPlans);
  if (plan) return formatPlanCheckoutTitle(plan);
  return cleanPlanCommercialLabel(client?.plan_display_label || client?.package_name || '—') || '—';
}

/** Nome do plano original na Sigma (migração). */
export function formatSigmaOriginPlan(client) {
  if (!client) return '';
  const name = String(
    client.sigma_plan_name || client.origin_plan_name || (clientNeedsPlanSync(client) ? client.package_name : '') || '',
  ).trim();
  if (!name || name.toUpperCase() === 'PLANO' || name.toUpperCase() === 'PLAN') return '';
  return name;
}
